import React from 'react';

import { useOnboardingStatus } from '@context/UsersContext';
import { addContacts } from '@db/Contacts';
import { getContacts, requestContactsPermissions } from '@helpers/Contacts';
import { createNamedContext, useRequiredContext } from '@helpers/Context';
import { sortByDisplayName } from '@helpers/Formatters';
import { logError } from '@helpers/Loggers';
import { DeviceContact } from '@types';

export type ContactsContextProps = {
  contacts: DeviceContact[];
  /** Request contact access, then load and upload the latest device contacts when granted. */
  requestPermissionAndSync: () => Promise<void>;
};

export const ContactsContext = createNamedContext<ContactsContextProps>({
  ContactsContext: undefined,
});

export function useContacts() {
  return useRequiredContext(ContactsContext);
}

async function loadAndUploadContacts() {
  const contacts = await getContacts();
  if (contacts.length > 0) {
    try {
      await addContacts(contacts);
    } catch (error) {
      // Keep local contacts usable when the Firestore upsert fails.
      logError(error);
    }
  }
  return sortByDisplayName(contacts);
}

export function ContactsProvider({ children }: React.PropsWithChildren) {
  const { onboarded } = useOnboardingStatus();
  const [contacts, setContacts] = React.useState<DeviceContact[]>([]);

  const requestPermissionAndSync = React.useCallback(async () => {
    const permissionGranted = await requestContactsPermissions();
    if (permissionGranted) {
      setContacts(await loadAndUploadContacts());
    }
  }, []);

  React.useEffect(() => {
    if (!onboarded) return undefined;

    let active = true;
    // Only load contacts after onboarding so the permissions screen has been presented.
    loadAndUploadContacts()
      .then((contacts) => {
        if (active) {
          setContacts(contacts);
        }
      })
      .catch((error) => logError(error));

    return () => {
      active = false;
    };
  }, [onboarded]);

  const value = { contacts, requestPermissionAndSync };

  return <ContactsContext.Provider value={value}>{children}</ContactsContext.Provider>;
}
